/**
 * PORTED (REWRITTEN) — RO-3-00d / #44471.
 * Origin: `excellent/app/projects/[projectSlug]/[sectionSlug]/_crm/forecast-view.tsx`.
 *
 * A REWRITE. The Next original was a server component holding two raw SQLite
 * statements against `crm_opportunities` plus a local `tableExists` guard, all
 * inside its render path (`shared-render-ownership.md` cause **A**). Both
 * statements and the guard moved VERBATIM behind `crm.views.forecast`
 * (`excellent/server/routes/crm.ts`) — including the degrade-to-zeros behaviour
 * for a workspace where Sales/CRM never migrated, which is a property of the
 * READ and therefore belongs on the server side of the seam.
 *
 * Changes, and only these:
 *   `import "server-only"`                  -> dropped (this host has no vault)
 *   `getDb().prepare(...)` × 2 + tableExists -> `useApi(crm.views.forecast, {})`
 *   `won.v`                                  -> `data.won_value_minor`
 *
 * The two client-side derivations (`openValue`, `weighted`) stay HERE, computed
 * from the returned rows exactly as the original computed them from its query
 * rows — moving them server-side would have changed numbers the reader can no
 * longer check against the visible book.
 *
 * The contract IS mounted on this host — this component is live, not a stub.
 */

import {
  StatStrip,
  type StatStripItem,
  DataTable,
  PageHeader,
  EmptyState,
  SimulateInline,
  formatUsd,
  formatDate,
  ObjectLink,
} from "@excellent/ui";
import { LineChart } from "@excellent/ui/icons";
import { useApi } from "@excellent/ui/api";
import { crm } from "@excellent/ui/api/contracts/crm";

/**
 * Customers ▸ Forecasting (Three-Layer PRD §5.8a / UI-UX-SPEC §5.2.4 — a REBUILD
 * view, NOT an orphaned mount). The net-new weighted-pipeline analytics over
 * `crm_opportunities`: open pipeline value, the probability-weighted forecast
 * (Σ value × probability), closed-won, and the open-deal book. Each deal opens at
 * /o/<uid> (deals resolve via the bridged `deal` commitment). StatCards +
 * DataTable per CLAUDE.md surface/table rules; money via the shared formatter.
 */
export function ForecastView(_props: { basePath: string }) {
  const { data, error, isLoading } = useApi(crm.views.forecast, {});
  const open = data?.open ?? [];
  const wonValueMinor = data?.won_value_minor ?? 0;

  const openValue = open.reduce((a, d) => a + (d.value_minor ?? 0), 0);
  const weighted = open.reduce(
    (a, d) => a + (d.value_minor ?? 0) * ((d.probability_pct ?? 0) / 100),
    0,
  );

  const stats: StatStripItem[] = [
    { title: "Open pipeline", value: formatUsd(openValue / 100) },
    { title: "Weighted forecast", value: formatUsd(weighted / 100) },
    { title: "Closed won", value: formatUsd(wonValueMinor / 100) },
    { title: "Open deals", value: String(open.length) },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Forecasting"
        description="Where the quarter lands — pipeline weighted by stage and close date."
      />
      {error ? (
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      ) : null}
      <StatStrip items={stats} variant="standalone" />
      {/* Simulation doorway (§6.5.7 harness): a context-seeded "what if?" right on
          the forecast — project the Outcome impact of a change in deal inflow. */}
      <SimulateInline
        scenario="inflow"
        kind="deal"
        prompt="What if deal inflow changes? Project the fulfilment impact of more (or fewer) deals."
        leverLabel="Deal inflow change %"
      />
      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading…</p>
      ) : open.length === 0 ? (
        <EmptyState
          icon={<LineChart className="size-5" />}
          title="No open deals"
          description="Open opportunities appear here, weighted by their stage probability, as your pipeline fills."
        />
      ) : (
        <DataTable
          rows={open}
          rowKey={(r) => r.uid}
          columns={[
            {
              key: "name",
              header: "Deal",
              render: (r) => (
                <ObjectLink uid={r.uid} from="customers" className="font-medium hover:underline">
                  {r.name}
                </ObjectLink>
              ),
            },
            {
              key: "value",
              header: "Value",
              render: (r) => formatUsd((r.value_minor ?? 0) / 100),
            },
            {
              key: "prob",
              header: "Probability",
              render: (r) => (r.probability_pct != null ? `${r.probability_pct}%` : "—"),
            },
            {
              key: "weighted",
              header: "Weighted",
              render: (r) =>
                formatUsd(((r.value_minor ?? 0) * ((r.probability_pct ?? 0) / 100)) / 100),
            },
            {
              key: "close",
              header: "Expected close",
              render: (r) => formatDate(r.expected_close_date),
            },
          ]}
        />
      )}
    </div>
  );
}
